import { inject, Injectable, signal } from '@angular/core';
import {
  EFreightForShipperStatus,
  getEmptyFreightBasicForShipper,
  getEmptyFreightDetailForShipper,
  IDigitizedDeliveryNote,
  IFreightBasicForShipper,
  IFreightDetailedForShipper,
  UUID,
  UUID_NULL,
} from '@okcargo-apps/shared-domain';
import { ICompany, ICostCenter } from '@okcargo-apps/shared-domain-ionic';
import {
  FreightShipperCommandService,
  FreightShipperQueryService,
  transformToCreateFreightWithStopsAndAssignationsCommand,
  transformToIFreightBasicForShipper,
  transformToIFreightDetailForShipper,
  transformToUpdateFreightCommand,
  transformToUpdateFreightStopCommand,
} from '@okcargo-apps/shared-infrastructure';
import { deepEqualCleaned } from '@okcargo-apps/utils';
import {
  AcceptFreightOfferCommand,
  CreateFreightBatchCommand,
  RejectFreightOfferCommand,
} from '@okcargo/command-processor';
import {
  CriteriaPayload,
  DocumentMetadataFieldNames,
  DocumentMetadataResponse,
  FilterCombinator,
  FilterComparator,
  FilterPayload,
  FindFreightQuery,
  FreightFieldNames,
  FreightStatus,
  OrderPayload,
  OrderType,
  SearchDocumentMetadataQuery,
  SearchInProgressFreightsQuery,
  SearchNotInProgressFreightsQuery,
} from '@okcargo/query-processor';
import { BehaviorSubject, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { AccountService } from '../account/account.service';
import { DownloadDocumentService } from '../download-document/download-document.service';

@Injectable({
  providedIn: 'root',
})
export class FreightShipperService {
  private readonly accountService = inject(AccountService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly freightShipQueryService = inject(FreightShipperQueryService);
  private readonly freightShipCommandService = inject(FreightShipperCommandService);

  page = 0;
  size = 300;

  private readonly _accountCompanySubject = new BehaviorSubject<ICompany | null>(null);
  private _companyId: UUID | undefined;

  private readonly _freightsShipPending$ = new BehaviorSubject<IFreightBasicForShipper[]>([]);
  private readonly _freightsShipInProgress$ = new BehaviorSubject<IFreightBasicForShipper[]>([]);
  private readonly _freightsShipFinished$ = new BehaviorSubject<IFreightBasicForShipper[]>([]);

  private readonly _lastRefreshFreightShipDetailTimestamp = signal<number>(Date.now());

  // The loaded freight for comparisons when saving
  private readonly _originalFreightDetail: IFreightDetailedForShipper = getEmptyFreightDetailForShipper();

  public freightsShipNotFinishedLoading = signal(true);
  public freightsShipFinishedLoading = signal(true);

  constructor() {
    this.accountService.account$.subscribe((account) => {
      if (account.company) {
        this._companyId = account.company.id;
        this._accountCompanySubject.next(account.company);
      }
    });
  }

  public get freightsShipPending$(): BehaviorSubject<IFreightBasicForShipper[]> {
    return this._freightsShipPending$;
  }

  public get freightsShipInProgress$(): BehaviorSubject<IFreightBasicForShipper[]> {
    return this._freightsShipInProgress$;
  }

  public get freightsShipFinished$(): BehaviorSubject<IFreightBasicForShipper[]> {
    return this._freightsShipFinished$;
  }

  public get costCenters$(): Observable<ICostCenter[]> {
    return this._accountCompanySubject.asObservable().pipe(map((company) => company?.costCenters || []));
  }

  // TODO use it in components template (freightDetail)
  public get lastRefreshFreightShipDetailTimestamp(): number {
    return this._lastRefreshFreightShipDetailTimestamp();
  }

  public refreshFreightsShipNotFinished(): void {
    this.searchFreightsShipNotFinished();
  }

  public refreshFreightsShipFinished(): void {
    this.searchFreightsShipFinished();
  }

  public refreshAfterSaveData(): void {
    setTimeout(() => {
      this.refreshFreightsShipFinished();
      this.refreshFreightsShipNotFinished();
    }, 2000); // 2 sec
  }

  public refreshFreightShipDetail(): void {
    // Trigger/modifies the signal's content so that the component (they has
    // the freight id) makes the corresponding call in the constructor's effect()
    this._lastRefreshFreightShipDetailTimestamp.set(Date.now());
  }

  public findFreightShipDetail$(id: string): Observable<IFreightDetailedForShipper> {
    const query = {
      id: id,
    } as FindFreightQuery;

    return from(this.freightShipQueryService.findFreight(query)).pipe(
      switchMap((freightDetailedResponse): Observable<IFreightDetailedForShipper> => {
        const freight: IFreightDetailedForShipper = getEmptyFreightDetailForShipper();
        const transformedFreight = transformToIFreightDetailForShipper(freightDetailedResponse, freight);

        return this.searchFreightDetailDocuments$(freightDetailedResponse.id).pipe(
          map((documents) => {
            console.log('findFreightShipDetail$ documents to map => ', documents);

            // TODO review this, mapper necessary??? probably yes
            const deliveryNotes = documents.filter((document) => document.type === 'DELIVERY_NOTE');
            transformedFreight.digitizedDeliveryNotes = deliveryNotes as unknown as IDigitizedDeliveryNote[];
            return transformedFreight;
          }),
        );
      }),
    );
  }

  public async acceptPrice(freight: any): Promise<void> {
    const command = {
      assignationId: freight.shipperCarrierAssignationId,
      freightId: freight.id,
    } as AcceptFreightOfferCommand;
    this.freightShipCommandService.acceptFreightOffer(command);
  }

  public async rejectPrice(freight: any): Promise<void> {
    const command = {
      freightId: freight.id,
      reason: '',
    } as RejectFreightOfferCommand;
    this.freightShipCommandService.rejectFreightOffer(command);
  }

  public async updateOrCreateFreight(freight: IFreightDetailedForShipper): Promise<{
    saved: boolean;
    isNew: boolean;
    generalDataUpdated: boolean;
    updatedStopIndexes: number[];
  }> {
    const result = {
      saved: false,
      isNew: freight.id === UUID_NULL,
      generalDataUpdated: false,
      updatedStopIndexes: [] as number[],
    };

    if (deepEqualCleaned(freight, this._originalFreightDetail)) {
      return result;
    }

    const account = this._accountCompanySubject.getValue();

    console.log('account', account);

    if (!account) {
      console.error('Account not available');
      throw new Error('Account not available');
    }

    if (freight.id === UUID_NULL) {
      // is create
      const command = transformToCreateFreightWithStopsAndAssignationsCommand(freight, account);

      try {
        await this.freightShipCommandService.createFreightWithStopsAndAssignations(command);
        result.saved = true;
      } catch (error) {
        // TODO show error in components
        console.error('Error in create freight. ' + (error instanceof Error ? error.message : 'Unknown error'));
        throw new Error('Error in create freight. ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    } else {
      // is update
      try {
        const pendingCommands: Promise<any>[] = [];

        const freightWithoutStops = { ...freight, stops: [] };
        const originalWithoutStops = { ...this._originalFreightDetail, stops: [] };

        if (!deepEqualCleaned(freightWithoutStops, originalWithoutStops)) {
          const updateFreightCommand = transformToUpdateFreightCommand(freight);
          // pendingCommands.push(this.freightShipCommandService.updateFreight(updateFreightCommand));

          await new Promise((resolve) => setTimeout(resolve, 500));

          this.freightShipCommandService.updateFreight(updateFreightCommand);

          result.generalDataUpdated = true;
        }

        let delay = 500;

        freight.stopsDetailed.forEach((stop, index) => {
          const originalStop = this._originalFreightDetail.stopsDetailed[index];

          if (!originalStop || !deepEqualCleaned(stop, originalStop)) {
            const stopCommand = transformToUpdateFreightStopCommand(stop, index, freight.id);
            // pendingCommands.push(this.freightShipCommandService.updateFreightStop(stopCommand));
            // this.freightShipCommandService.updateFreightStop(stopCommand);

            delay += 500;

            setTimeout(() => {
              this.freightShipCommandService.updateFreightStop(stopCommand);
            }, delay);

            result.updatedStopIndexes.push(index);
          }
        });

        // if (pendingCommands.length > 0) {
        // await Promise.all(pendingCommands);
        result.saved = true;
        // }
      } catch (error) {
        console.error('Error in update freight. ' + (error instanceof Error ? error.message : 'Unknown error'));
        throw new Error('Error in update freight. ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }

    return result;
  }

  public async uploadFreightFile(fileInput: File): Promise<string> {
    if (!fileInput) {
      throw new Error('File is required');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (readerEvent: ProgressEvent<FileReader>) => {
        try {
          const fileData = (readerEvent.target as FileReader).result as string;
          const base64Data = fileData.substring(fileData.indexOf(',') + 1);

          const command = {
            data: base64Data,
          } as CreateFreightBatchCommand;

          await this.freightShipCommandService.createFreightBatch(command);
          resolve(fileInput.name);
        } catch (error) {
          console.warn('Error uploading file:', error);
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(fileInput);
    });
  }

  // -- private

  private searchFreightsShipNotFinished(): void {
    const query = {
      criteriaPayload: {
        order: [
          {
            field: FreightFieldNames.LATEST_STARTING_AT,
            type: OrderType.ASC,
          } as OrderPayload,
        ],
        // only freights assigned to user companyID (as a shipper)
        filters: [
          {
            combinator: FilterCombinator.NONE,
            field: FreightFieldNames.SHIPPER_ID,
            comparator: FilterComparator.EQ,
            value: this._companyId,
            group: null,
          },
          {
            combinator: FilterCombinator.AND,
            field: FreightFieldNames.STATUS,
            comparator: FilterComparator.IN,
            value: FreightStatus.PLANNING + '|' + FreightStatus.CREATED + '|' + FreightStatus.ACCEPTED + '|',
            group: null,
          },
        ],
        pageIndex: this.page,
        pageSize: this.size,
      } as unknown as CriteriaPayload,
    } as SearchInProgressFreightsQuery;

    from(this.freightShipQueryService.searchInProgressFreights(query))
      .pipe(
        map((freights) =>
          freights.map((freightBasicResponse): IFreightBasicForShipper => {
            const freight: IFreightBasicForShipper = getEmptyFreightBasicForShipper();
            return transformToIFreightBasicForShipper(freightBasicResponse, freight);
          }),
        ),
        map((freights) => {
          // TODO review if freight.status !== EFreightStatus.FINISHED is necessary because there were times when the status of the last stop was not consistent with the freight status
          const pendingFreights = freights.filter(
            (freight) =>
              freight.status === EFreightForShipperStatus.OFFER_REQUESTED ||
              freight.status === EFreightForShipperStatus.OFFER_SENT,
          );

          const inProgressFreights = freights.filter(
            (freight) =>
              freight.status !== EFreightForShipperStatus.OFFER_REQUESTED &&
              freight.status !== EFreightForShipperStatus.OFFER_SENT,
          );

          return { pendingFreights, inProgressFreights };
        }),
      )
      .subscribe(({ pendingFreights, inProgressFreights }) => {
        this._freightsShipPending$.next(pendingFreights);
        this._freightsShipInProgress$.next(inProgressFreights);
        this.freightsShipNotFinishedLoading.set(false);
      });
  }

  private searchFreightsShipFinished(): void {
    const query = {
      criteriaPayload: {
        order: [
          {
            field: FreightFieldNames.LATEST_STARTING_AT,
            type: OrderType.DESC,
          } as OrderPayload,
        ],
        // only freights assigned to others
        filters: [
          {
            combinator: FilterCombinator.NONE,
            field: FreightFieldNames.SHIPPER_ID,
            comparator: FilterComparator.EQ,
            value: this._companyId,
            group: null,
          },
          {
            combinator: FilterCombinator.AND,
            field: FreightFieldNames.STATUS,
            comparator: FilterComparator.IN,
            value: FreightStatus.FINISHED + '|' + FreightStatus.CANCELLED,
            group: null,
          },
        ],
        pageIndex: this.page,
        pageSize: this.size,
      } as unknown as CriteriaPayload,
    } as SearchNotInProgressFreightsQuery;

    from(this.freightShipQueryService.searchFinishedFreights(query))
      .pipe(
        map((freights) =>
          freights.map((freightBasicResponse): IFreightBasicForShipper => {
            const freight: IFreightBasicForShipper = getEmptyFreightBasicForShipper();
            return transformToIFreightBasicForShipper(freightBasicResponse, freight);
          }),
        ),
      )
      .subscribe((transformedFreights) => {
        this._freightsShipFinished$.next(transformedFreights);
        this.freightsShipFinishedLoading.set(false);
      });
  }

  private searchFreightDetailDocuments$(freightId: string): Observable<DocumentMetadataResponse[]> {
    const query = {
      criteriaPayload: {
        order: [],
        filters: [
          {
            field: DocumentMetadataFieldNames.AGGREGATE_ID,
            comparator: FilterComparator.EQ,
            value: freightId,
            combinator: FilterCombinator.NONE,
          } as FilterPayload,
        ], // FilterPayload[]; create a filter by history or in-progress
        pageIndex: 0,
        pageSize: 30,
      } as unknown as CriteriaPayload,
    } as SearchDocumentMetadataQuery;

    return from(this.freightShipQueryService.searchDocumentMetadata(query)).pipe(
      switchMap((documents: DocumentMetadataResponse[]) => {
        if (documents.length === 0) {
          return of([]);
        }

        const documentObservables = documents.map((document) => {
          const url = this.downloadDocumentService.getDownloadDocumentURL(document);
          return of({ ...document, url });
        });
        return forkJoin(documentObservables);
      }),
    );
  }
}
